import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppRoutes } from '@app';
import { formatCompanyName, formatFullName } from '@core';
import {
  IApiError,
  IIntInvoiceRemoveFromRegisterPayload,
  InternationalInvoice,
  Invoice,
  PaymentCostsInfo,
} from '@models';
import { AlertToastService, AuthService, BreakpointService, InternationalInvApiService } from '@services';
import {
  EIIPrintForm,
  FullscreenMobileModalConfig,
  getPaidServicesForPaymentStatusCheck,
  getServicesForPaymentStatusCheck,
  isSomeServicePaid,
  isZebraPrintAvailable,
  Unsubscriber,
} from '@shared';
import { MasterpassPaymentComponent } from 'app/components/dashboard/acquiring/components/masterpass-payment/masterpass-payment.component';
import { RegisterCreateResponse } from 'app/models/register.model';
import { IntRegistersApiService } from 'app/services/int-registers-api/int-registers-api.service';
import { InformerPosition, InvoiceStateStatus } from 'app/services/invoices/invoice-states.config';
import { InvoiceService } from 'app/services/invoices/invoice.service';
import { MasterpassService } from 'app/services/masterpass.service';
import { catchError, delay, finalize, of, switchMap } from 'rxjs';
import { ConfirmDialogModernConfig } from '../confirm-dialog-modern/confirm-dialog-modern.component';
import { ConfirmDialogModernService } from '../confirm-dialog-modern/confirm-dialog-modern.service';
import {
  NewInternationalRegisterComponent,
  NewInternationalRegisterMode,
} from '../registries/registers-international/components/new-international-register';
import {
  getPayerType,
  getPaymentMethod,
  IIDeleteButtonInitialState,
  IIEditButtonInitialState,
  PaymentMethods,
  SenderPayerRef,
} from './show-invoice-my-international.constants';
import { IShowInvoiceMyInternationalDialogData } from './show-invoice-my-international.models';

@Component({
  selector: 'app-show-invoice-international',
  templateUrl: './show-invoice-my-international.component.html',
  styleUrls: ['./show-invoice-my-international.component.scss'],
})
export class ShowInvoiceMyInternationalComponent extends Unsubscriber implements OnInit {
  protected readonly InvoiceStateStatus = InvoiceStateStatus;
  protected readonly InformerPosition = InformerPosition;
  protected readonly PRINT_FORMS = EIIPrintForm;
  protected readonly isZebraPrintAvailable = isZebraPrintAvailable;
  protected readonly DELAY_TIME = 500;
  protected readonly formatFullName = formatFullName;
  protected readonly formatCompanyName = formatCompanyName;

  shouldReloadGrid: boolean = false;
  paymentData: PaymentCostsInfo = null;
  payButtonTooltip: string = '';
  isPaymentDisabled: boolean = false;
  isPaymentButtonVisible: boolean = false;
  isPaymentCancelButtonVisible: boolean = false;
  isPaymentPending: boolean = false;
  isCancelPaymentPending: boolean = false;
  isDataLoading: boolean = false;
  isPrintLoading: boolean = false;
  isShowFull: boolean = false;
  paymentPopupOpened: boolean = false;
  isPayPaid: boolean = false;
  printZebraTooltip: string = '';
  editDisabled: boolean = IIEditButtonInitialState;
  deleteDisabled: boolean = IIDeleteButtonInitialState;
  weight: number = null;
  volumetricWeight: number = null;

  isMobile$ = this.breakpointService.isMobile$;
  isDesktop$ = this.breakpointService.isDesktop$;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IShowInvoiceMyInternationalDialogData,
    private dialogRef: MatDialogRef<ShowInvoiceMyInternationalComponent>,
    private dialog: MatDialog,
    private masterPassService: MasterpassService,
    private intRegistersApiService: IntRegistersApiService,
    private authService: AuthService,
    private alertToastService: AlertToastService,
    private confirmService: ConfirmDialogModernService,
    private internationalInvApiService: InternationalInvApiService,
    private router: Router,
    private invoiceService: InvoiceService,
    private breakpointService: BreakpointService,
  ) {
    super();
    console.log('ShowInvoiceMyInternationalComponent data', data);
  }

  get isLoadingVisible(): boolean {
    return this.isPaymentPending || this.isDataLoading || this.isPrintLoading;
  }

  get invoice(): InternationalInvoice {
    return this.data?.invoice;
  }

  get SenderAddress(): string {
    if (this.invoice.CounterpartySenderFromWarehouse) {
      return [
        this.invoice.CitySenderDescription,
        this.invoice.WarehouseSenderDescription,
      ].join(', ');
    }

    return [
      this.invoice.CitySenderDescription,
      this.invoice.CounterpartySenderStreet,
      this.invoice.CounterpartySenderHouse,
      this.invoice.SenderFlat,
    ].join(', ');
  }

  get Recipient(): string {
    if (this.invoice.CounterpartyTypeRecipient === 'Organization') {
      return [
        this.invoice.CounterpartyRecipient,
        this.invoice.ContactPersonRecipient,
      ].filter(v => !!v).join(' ');
    }

    return this.invoice.ContactPersonRecipient;
  }

  get RecipientAddress(): string {
    return [
      this.invoice.CountryRecipientDescriptionEn,
      this.invoice.CityRecipient,
      this.invoice.CounterpartyRecipientStreet,
      this.invoice.CounterpartyRecipientHouse,
      this.invoice.CounterpartyRecipientApart,
    ].filter(v => !!v).join(', ');
  }

  get Payer(): string {
    return getPayerType(this.invoice.PayerType);
  }

  get PaymentMethod(): string {
    return getPaymentMethod(this.invoice.PaymentMethod);
  }

  get isPaid(): boolean {
    return this.allServicesArePaid();
  }

  switchShowFull(): void {
    this.isShowFull = !this.isShowFull;
  }

  ngOnInit(): void {
    this.getDocumentData();
    this.calculateWeights();
  }

  pay(): void {
    this.paymentPopupOpened = true;
    const dialogRef = this.dialog.open(MasterpassPaymentComponent, {
      position: { right: '0' },
      width: '75%',
      height: '100%',
      panelClass: 'setting-content-popup',
    });
    dialogRef.componentInstance.type = 'int';
    dialogRef.componentInstance.inputInvoice = this.invoice as unknown as Invoice;
    dialogRef.componentInstance.invoiceData = {
      docNumber: this.invoice.Number,
      SendersPhone: this.invoice.PhoneSender,
      RecipientsPhone: this.invoice.PhoneRecipient,
      Amount: this.invoice.Cost,
      DeliveryType: 'InternationalDelivery',
    };
    dialogRef.componentInstance.paymentCostsInfo = this.paymentData;
    dialogRef.componentInstance.recipientCounterpartyType = this.invoice.CounterpartyTypeRecipient;
    dialogRef.componentInstance.isPaid = this.isPayPaid;
    dialogRef.componentInstance.user = this.authService.user;

    dialogRef.afterClosed().subscribe(() => {
      this.isPaymentPending = true;
      this.paymentPopupOpened = false;
      this.masterPassService
        .getPaymentInfo(
          this.invoice.Number,
          this.authService.user.phone || this.invoice.PhoneSender,
        )
        .pipe(finalize(() => this.isPaymentPending = false))
        .subscribe((paymentCostsInfo) => {
          this.shouldReloadGrid = true;
          this.paymentData = paymentCostsInfo;
          this.updatePaymentAvailability();
          this.updatePaymentCancelAvailability(paymentCostsInfo, this.invoice);
          this.updateActionsAvailability(this.paymentData);
          this.isPaymentPending = false;
        });
    });
  }

  close(): void {
    this.dialogRef.close({ success: this.shouldReloadGrid });
  }

  cancelPayment(): void {
    const invoiceNumber = this.invoice.Number;
    const senderPhone = this.authService.user.phone || this.invoice.PhoneSender;

    this.isCancelPaymentPending = true;
    this.masterPassService.cancelPayment([invoiceNumber])
      .pipe(
        // We use delay because  API needs some time to update payment status
        delay(this.DELAY_TIME),
        switchMap(() => this.masterPassService.getPaymentInfo(invoiceNumber, senderPhone)),
        finalize(() => this.isCancelPaymentPending = false),
      )
      .subscribe(
        paymentCostsInfo => {
          this.paymentData = paymentCostsInfo;
          this.updatePaymentAvailability();
          this.updatePaymentCancelAvailability(paymentCostsInfo, this.invoice);
          this.updateActionsAvailability(this.paymentData);
          this.alertToastService.pushSuccess('Успішно відмінено оплату');
          this.shouldReloadGrid = true;

          this.isPayPaid = false;
        },
        (error) => {
          if (error[0]) {
            this.alertToastService.pushApiErrors(error);
          }
        },
      );
  }

  duplicate(invoice: InternationalInvoice): void {
    this.dialogRef.close();
    this.router.navigate([AppRoutes.invoice_copy_international, invoice.Ref]);
  }

  edit(invoice: InternationalInvoice): void {
    this.dialogRef.close();
    this.router.navigate([AppRoutes.invoice_edit_international, invoice.Ref]);
  }

  remove(invoice: InternationalInvoice): void {
    this.internationalInvApiService.deleteInvoice(invoice.Ref).subscribe(
      (success) => {
        if (success) {
          this.dialogRef.close({ success: true });
        }
      },
      (errors: IApiError[]) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
        }
      },
    );
  }

  addToRegister(): void {
    const dialogRef = this.dialog.open(NewInternationalRegisterComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
          panelClass: 'no-padding',
        }),
      data: {
        canEditDate: false,
        canViewDate: false,
        canEditDescription: true,
      },
      autoFocus: false,
    });
    dialogRef.componentInstance.registers = this.data.registers;
    dialogRef.componentInstance.invoicesIds = [this.invoice.Ref];

    dialogRef
      .afterClosed()
      .subscribe((data: { register: RegisterCreateResponse; type: NewInternationalRegisterMode; success: boolean }) => {
        if (data?.success) {
          this.invoice.ScanSheetRef = data.register.Ref;
          this.invoice.ScanSheetNumber = data.register.Number;
          this.shouldReloadGrid = true;
        }
      });
  }

  removeSingleFromRegister(): void {
    const config: ConfirmDialogModernConfig = {
      title: `Ви справді хочете видалити цю накладну з реєстру?`,
      text: `Після видалення посилку можна додати до реєстру повторно`,
      confirmBtnText: 'Так, видалити',
      cancelBtnText: 'Ні, залишити',
      isAsync: true,
      callback: () => this.removeFromRegister(),
    };
    this.confirmService.confirm(config);
  }

  removeFromRegister(): void {
    const data: IIntInvoiceRemoveFromRegisterPayload = {
      Ref: this.invoice.ScanSheetRef,
      DocumentRefs: [this.invoice.Ref],
    };

    this.subscriptions = this.intRegistersApiService.removeFromRegister(data).pipe(
      catchError((errors) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
        }
        return of(false);
      }),
    ).subscribe((success) => {
      this.invoice.ScanSheetNumber = null;
      this.invoice.ScanSheetRef = null;
      this.shouldReloadGrid = true;
      this.confirmService.close();
      if (success) {
        this.alertToastService.pushSuccess('Успішно видалено з реєстру');
      }
    });
  }

  setPrintZebraTooltip(): void {
    this.printZebraTooltip = !isZebraPrintAvailable(this.data.invoice?.State) ? 'Друк маркування доступний лише для відправлень зі статусом «Верифіковано»' : null;
  }

  print(printForm: EIIPrintForm): void {
    const invoicesIds = [this.invoice.Ref];

    if (this.isPrintLoading) {
      return;
    }

    this.isPrintLoading = true;

    this.invoiceService.printDocuments(invoicesIds, 'pdf', {
      printForm,
    })
      .pipe(finalize(() => this.isPrintLoading = false))
      .subscribe(
        () => {},
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
        },
      );
  }

  private updatePaymentAvailability(): void {
    const useNovaPay = this.paymentData?.UseNovaPay;

    if (!useNovaPay) {
      this.payButtonTooltip = null;
      this.isPaymentDisabled = true;
      this.isPaymentButtonVisible = false;
      return;
    }

    if (this.invoice.PayerType !== SenderPayerRef) {
      this.payButtonTooltip = 'Онлайн-оплата доступна лише за умови вибору платником за доставку: Відправника';
      this.isPaymentDisabled = true;
      this.isPaymentButtonVisible = true;
      return;
    }

    const payerIsSender = this.invoice.PayerType === SenderPayerRef;
    const servicesToCheck = getServicesForPaymentStatusCheck(this.paymentData);
    const unpaidServices = servicesToCheck.some(service => service !== 0);
    const cashPaymentMethod = this.invoice.PaymentMethod === PaymentMethods.Cash;
    if (payerIsSender && useNovaPay && unpaidServices && cashPaymentMethod) {
      this.isPaymentDisabled = false;
      this.isPaymentButtonVisible = true;
      return;
    }

    const allServicesArePaid = this.allServicesArePaid();
    if (allServicesArePaid) {
      this.isPaymentButtonVisible = false;
    }
  }

  private updatePaymentCancelAvailability(paymentCostsInfo: PaymentCostsInfo, invoice: InternationalInvoice): void {
    if (!paymentCostsInfo || !invoice) {
      this.isPaymentCancelButtonVisible = false;
      return;
    }
    const payerIsSender = invoice.PayerType === SenderPayerRef;
    const paymentMethodIsCash = invoice.PaymentMethod === PaymentMethods.Cash;
    // NovaPay не дозволяє оплату
    const paymentAllowedByNovaPay = paymentCostsInfo.UseNovaPay === null;
    const allServicesArePaid = this.allServicesArePaid();

    this.isPaymentCancelButtonVisible = payerIsSender
      && paymentMethodIsCash
      && paymentAllowedByNovaPay
      && allServicesArePaid;
  }

  private allServicesArePaid(): boolean {
    const servicesToCheck = getServicesForPaymentStatusCheck(this.paymentData);
    const serviceIsPaid = service => service === 0;

    return servicesToCheck.every(service => serviceIsPaid(service));
  }

  private updateActionsAvailability(paymentInfo: PaymentCostsInfo): void {
    if (!paymentInfo) {
      this.editDisabled = IIEditButtonInitialState;
      this.deleteDisabled = IIDeleteButtonInitialState;
    } else {
      const services = getPaidServicesForPaymentStatusCheck(paymentInfo);

      const someServiceIsPaid = isSomeServicePaid(services);
      this.editDisabled = this.deleteDisabled = someServiceIsPaid;
    }
  }

  private getDocumentData(): void {
    this.isDataLoading = true;
    this.masterPassService.getPaymentInfo(this.invoice.Number, '')
      .pipe(finalize(() => this.isDataLoading = false))
      .subscribe(paymentCostsInfo => {
        this.paymentData = paymentCostsInfo;
        this.updatePaymentAvailability();
        this.updateActionsAvailability(this.paymentData);
        this.updatePaymentCancelAvailability(paymentCostsInfo, this.invoice);
        this.setPrintZebraTooltip();
      });
  }

  private calculateWeights(): void {
    if (!this.invoice) {
      return;
    }
    const { weight, volumetricWeight } = this.invoice.Places.reduce(
      (acc, place) => ({
        weight: acc.weight + place.ActualWeight,
        volumetricWeight: acc.volumetricWeight + place.VolumetricWeight,
      }),
      { weight: 0, volumetricWeight: 0 },
    );

    this.weight = weight;
    this.volumetricWeight = volumetricWeight;
  }
}
